import { v4 as uuidv4 } from 'uuid';
import { PAYMENT_SESSION_STORAGE_KEY, PAYMENT_SESSION_TTL_MS } from './config.js';
import type { PaymentSessionConfig } from './ports.js';

interface SessionEntry {
	id: string;
	expiresAt: number;
}

export interface PaymentSession {
	/**
	 * Returns the current payment-session id, minting a fresh UUID v4 when none
	 * exists or the cached/stored one has passed its TTL. Stable within the TTL so
	 * the Signifyd script tag and the payment payload always agree.
	 */
	getPaymentSessionId(): string;
	/** Clears the in-memory and persisted id so the next call mints anew. */
	resetPaymentSessionId(): void;
}

const isValidSession = (entry: unknown): entry is SessionEntry =>
	!!entry &&
	typeof (entry as SessionEntry).id === 'string' &&
	typeof (entry as SessionEntry).expiresAt === 'number' &&
	(entry as SessionEntry).expiresAt > Date.now();

/**
 * Creates a payment-session id source backed by the injected storage. Callers
 * should create ONE instance per client and share it between the Signifyd loader
 * and the payment-payload builder so both read the same id. Persistence via the
 * injected `sessionStorage` lets the id survive same-session navigations.
 * @param config Host environment (browser flag + storage).
 * @returns The session accessor.
 */
export function createPaymentSession(config: PaymentSessionConfig): PaymentSession {
	let cached: SessionEntry | null = null;

	const mint = (): string => {
		const entry: SessionEntry = { id: uuidv4(), expiresAt: Date.now() + PAYMENT_SESSION_TTL_MS };
		cached = entry;
		if (config.isBrowser && config.storage) {
			try {
				config.storage.setItem(PAYMENT_SESSION_STORAGE_KEY, JSON.stringify(entry));
			} catch {
				/* storage unavailable (private mode / quota) — memory cache still holds */
			}
		}
		return entry.id;
	};

	const getPaymentSessionId = (): string => {
		if (isValidSession(cached)) return cached.id;
		if (!config.isBrowser || !config.storage) return mint();
		try {
			const raw = config.storage.getItem(PAYMENT_SESSION_STORAGE_KEY);
			if (raw) {
				const parsed: unknown = JSON.parse(raw);
				if (isValidSession(parsed)) {
					cached = parsed;
					return parsed.id;
				}
			}
		} catch {
			/* corrupt entry — fall through and mint a fresh id */
		}
		return mint();
	};

	const resetPaymentSessionId = (): void => {
		cached = null;
		if (config.isBrowser && config.storage) {
			try {
				config.storage.removeItem(PAYMENT_SESSION_STORAGE_KEY);
			} catch {
				/* nothing to clear */
			}
		}
	};

	return { getPaymentSessionId, resetPaymentSessionId };
}
